import {
  ProductTypeName,
  ShippingMethod,
  WarehouseShippingMethod,
} from '@electricjs/core_entity-client';

import { Country } from '@electricjs/arc';

import { StorefrontProductTypeGroup } from '@/types/storefront';

//
// ProductTypes grouping configuration
//

/** The correspondent product types for each type group */
export const PRODUCT_TYPES_BY_TYPE_GROUP: Record<
  StorefrontProductTypeGroup,
  ProductTypeName[]
> = {
  [StorefrontProductTypeGroup.Laptops]: [ProductTypeName.Laptop],
  [StorefrontProductTypeGroup.Monitors]: [ProductTypeName.Monitor],
  [StorefrontProductTypeGroup.Desktops]: [ProductTypeName.Desktop],
  [StorefrontProductTypeGroup.Tablets]: [ProductTypeName.Tablets],
  [StorefrontProductTypeGroup.Phones]: [
    ProductTypeName.CellPhone,
    ProductTypeName.OtherPhone,
  ],
  [StorefrontProductTypeGroup.KeyboardMouse]: [
    ProductTypeName.Keyboard,
    ProductTypeName.Mouse,
    ProductTypeName.KeyboardMouse, // it's the keyboard+mouse combo
  ],
  [StorefrontProductTypeGroup.Docks]: [ProductTypeName.Dock],
  [StorefrontProductTypeGroup.AudioVideo]: [
    ProductTypeName.Audio, // keeping temporarily for backward compatibility. It will be replaced by Headphones, Microphone
    ProductTypeName.Headphones,
    ProductTypeName.Microphone,
    ProductTypeName.Video,
  ],
  [StorefrontProductTypeGroup.Misc]: [
    ProductTypeName.Miscellaneous, // keeping temporarily for backward compatibility. It will be replaced by PowerAdapter, Cable, Accessory
    ProductTypeName.PowerAdapter,
    ProductTypeName.Cable,
    ProductTypeName.Accessory,
  ],
};

/**
 *  The type groups that will have the special "TYPE" filter (to allow granular selection of its product types).
 *  It's added by the frontend as a special dynamic filter.
 */
export const TYPE_GROUPS_WITH_SPECIAL_TYPE_FILTER: StorefrontProductTypeGroup[] =
  [StorefrontProductTypeGroup.KeyboardMouse, StorefrontProductTypeGroup.Phones];

//
// Installments configuration
//

type InstallmentConfig = {
  [installmentsCount: number]: {
    /**Interest value in percentage(e.g. 6 would be 6%) */
    interestPercentage: number;
  };
};

/** Installments configuration */
export const INSTALLMENTS_CONFIG: InstallmentConfig = {
  12: {
    interestPercentage: 15,
  },
  24: {
    interestPercentage: 30,
  },
};

//
// Order processing configuration
//

/** Order processing days */
export const ORDER_PROCESSING_DAYS = 3;

/** warehouse order processing days */
export const WAREHOUSE_ORDER_PROCESSING_DAYS = 3;

/** ZTP handling days */
export const ZTP_HANDLING_DAYS = 6;

/** WAutopilot handling days */
export const WAUTOPILOT_HANDLING_DAYS = 1;

//
// Shipping configuration
//

/** Current available catalog shipping method for storefront (in that sequence, for each country) */
export const CURRENT_CATALOG_SHIPPING_METHODS: {
  [country in Country]?: { method: ShippingMethod; deliveryDays: number }[];
} = {
  US: [
    {
      method: ShippingMethod.UpsGround,
      deliveryDays: 3,
    },
    {
      method: ShippingMethod.UpsNextDayAir,
      deliveryDays: 1,
    },
  ],
  CA: [
    // TODO: TD Synnex Canada doesn't support UPS expedited shipping, so Canada only offers ground until the Purolator tracking integration is implemented
    {
      method: ShippingMethod.UpsGround,
      deliveryDays: 3,
    },
  ],
};

/** Current available warehouse shipping method options for storefront (in that sequence, for each country) */
export const CURRENT_WAREHOUSE_SHIPPING_METHODS: {
  [country in Country]?: {
    method: WarehouseShippingMethod;
    deliveryDays: number;
  }[];
} = {
  US: [
    {
      method: WarehouseShippingMethod.Ground,
      deliveryDays: 3,
    },
    {
      method: WarehouseShippingMethod.Expedited,
      deliveryDays: 1,
    },
  ],
  CA: [
    // for now, Canada has the same shipping methods of United States, with the same delivery days
    {
      method: WarehouseShippingMethod.Ground,
      deliveryDays: 3,
    },
    {
      method: WarehouseShippingMethod.Expedited,
      deliveryDays: 1,
    },
  ],
};

//
// International configuration
//

/** Minimum number of days to process the international order and have it delivered */
export const MIN_INTERNATIONAL_ORDER_DAYS = 5;

/** Extra days added to the Tecspal max lead time to account for vendor communication before shipping */
export const TECSPAL_VENDOR_COMMUNICATION_BUFFER_DAYS = 2;

/** Current shopping countries for storefront (in that sequence) */
export const CURRENT_SHOPPING_COUNTRY_CODES: Country[] = ['US']; // 'CA' will be automatically added by the Storefront component, based on the feature-flag

/** Current supported countries for Jumpcloud Windows ZTP */
export const CURRENT_JUMPCLOUD_WIN_ZTP_COUNTRY_CODES: Country[] = ['US'];

/** Current supported countries for Windows Autopilot */
export const CURRENT_WIN_AUTOPILOT_COUNTRY_CODES: Country[] = ['US'];
